import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { FileTypeKey, FileTypeValue } from '../types/FileType';
import useIonicToast from './useIonicToast';

type Props = {
  maxSize: number;
  maxFiles: number;
  files: File[];
  disabled?: boolean;
  acceptableType: Partial<Record<FileTypeKey, Array<FileTypeValue>>>;
  onDrop:
    | (<T extends File>(
        acceptedFiles: T[],
        fileRejections: FileRejection[],
        event: DropEvent
      ) => void)
    | undefined;
};

export default function useFileDropZone({
  maxSize,
  maxFiles,
  files,
  acceptableType,
  disabled,
  onDrop,
}: Props) {
  const { presentIonToast } = useIonicToast();
  const fileValidator = (file: File) => {
    if (!file || !maxSize) return null;
    const sizeInMb = Math.floor(file.size / 1024 ** 2);
    if (sizeInMb > maxSize)
      return {
        code: 'size-too-large',
        message: `${file.name} size is larger than ${sizeInMb}mb`,
      };
    return null;
  };

  const dropzone = useDropzone({
    onDrop,
    accept: acceptableType,
    maxFiles: maxFiles - files.length,
    validator: fileValidator,
    disabled: files.length >= maxFiles || disabled,
    onError: (error) => presentIonToast(error.message, 'error'),
    onDropRejected: (fileRejections: FileRejection[]) => {
      fileRejections.forEach(({ file: _, errors }) => {
        errors.forEach((err) => presentIonToast(`${err.message}`, 'error'));
      });
    },
  });

  return dropzone;
}
